/**
 * The form layer: `FormSectionCard`, `FormField`, `LiabilityTag`, and the create forms'
 * `MissingFieldsButton`.
 *
 * A field renders from its `FieldDef`, so the required marker, the liable party,
 * the Excel column name and the "not printed on the PDF" note all come from the
 * dictionary rather than from each screen's own idea of them.
 *
 * Two things about errors:
 *
 *  - **The server owns validation.** Client-side checks are a fast hint only;
 *    `serverErrors` (from the API's `partyErrors`) is what actually decides, and
 *    it wins whenever both are present.
 *  - **`WARNING` never blocks.** NIF and plate checksums come back as warnings
 *    because foreign operators and edge formats exist, and refusing a legally
 *    required document over a checksum would be worse than the risk. They are
 *    rendered in the warning colour and never gate the submit button.
 */

import { createContext, useContext, useId, useMemo, type ReactNode, type RefObject } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { UNIDADES_MEDIDA, type FieldError as ApiFieldError, type LiableParty } from '@deca/api'

import { Icon } from './Icon.tsx'
import { PartyCombobox } from './PartyCombobox.tsx'
import type { PartyField } from './partyFill.tsx'
import { BookCombobox } from './BookCombobox.tsx'
import { columnOf, type BookField } from './bookField.ts'
import { Button } from './primitives.tsx'
import { fieldDef, qualifiedFieldLabel, type FieldDef, type FieldSection } from '../deca/fields.ts'
import { SERVICE_DATE_BOUNDS } from '../deca/format.ts'

/**
 * What a form primitive actually needs from a field definition. Structural on
 * purpose: `FieldDef` and `PassengerFieldDef` are separate types because the two
 * documents are separate legal instruments, and this is the narrow overlap where
 * they can be rendered by the same control.
 */
export interface FieldDefLike {
  column: string
  labelEs: string
  mandatory: boolean
  onPdf: boolean
  multiline?: boolean
}

/* ── LiabilityTag ───────────────────────────────────────────────────────── */

const PARTY_CLASS: Record<LiableParty, string> = {
  CARGADOR: 'liability--cargador',
  TRANSPORTISTA: 'liability--transportista',
  CUALQUIERA: 'liability--cualquiera',
}

export function LiabilityTag({ party }: { party: LiableParty }) {
  const { t } = useTranslation()
  return (
    <span className={`liability ${PARTY_CLASS[party]}`} title={t('party.penalty', { amount: 401 })}>
      {t(`party.${party}`)}
    </span>
  )
}

/* ── form context ───────────────────────────────────────────────────────── */

/**
 * Which dictionary a form speaks: how to resolve a column to its definition,
 * and how to number and label its sections. One object rather than three
 * independent optionals, because the three are one concept — a caller that
 * could supply `lookup` and `sectionNumber` separately could supply an
 * *inconsistent* pair (a passenger `lookup` with the goods section numbers),
 * which type-checks and renders wrong rather than failing to compile.
 */
export interface FormDictionary {
  lookup: (column: string) => FieldDefLike | undefined
  sectionNumber: Record<string, number>
  sectionLabelKey: (section: string) => string
}

interface DecaFormState {
  /** Field-keyed server errors, from `errorsByField(problem)`. */
  serverErrors: Record<string, ApiFieldError>
  /** Columns changed by the amendment being composed — highlighted like the PDF. */
  modified?: ReadonlySet<string>
  /** Read-only rendering, for the fields an in-place amendment may not touch. */
  lockedColumns?: ReadonlySet<string>
  /**
   * Which dictionary this form speaks. Supplied rather than imported, because
   * there are two document types and a form primitive that knows only one of
   * them throws on the other's columns. Optional and resolved to goods exactly
   * once — inside `DecaFormProvider`, see `GOODS_DICTIONARY` below — so every
   * existing call site keeps its behaviour without restating it.
   */
  dictionary?: FormDictionary
}

/** `DecaFormState` with `dictionary` resolved — what the context actually holds. */
interface ResolvedFormState extends DecaFormState {
  dictionary: FormDictionary
}

const SECTION_NUMBER: Record<FieldSection, number> = {
  cargador: 1,
  transportista: 2,
  envio: 3,
  transporte: 4,
}

const GOODS_DICTIONARY: FormDictionary = {
  lookup: fieldDef,
  sectionNumber: SECTION_NUMBER,
  sectionLabelKey: (section) => `sections.${section}`,
}

const DecaFormContext = createContext<ResolvedFormState>({
  serverErrors: {},
  dictionary: GOODS_DICTIONARY,
})

export function DecaFormProvider({
  value,
  children,
}: {
  value: DecaFormState
  children: ReactNode
}) {
  // The one place the goods default is substituted — see the note on
  // `DecaFormState.dictionary` above for why this used to be three independent
  // `??`s (one per field, at each read site) and no longer is.
  const resolved = useMemo<ResolvedFormState>(
    () => ({ ...value, dictionary: value.dictionary ?? GOODS_DICTIONARY }),
    [value],
  )
  return <DecaFormContext.Provider value={resolved}>{children}</DecaFormContext.Provider>
}

/* ── FormSectionCard ────────────────────────────────────────────────────── */

export function FormSectionCard({
  section,
  invalid,
  actions,
  children,
}: {
  section: string
  invalid?: boolean
  actions?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  const { dictionary } = useContext(DecaFormContext)
  // `section` is a free-form `string` now (see the widened prop above) — nothing
  // upstream validates it against the active dictionary before it gets here, so
  // this lookup itself is the check. `noUncheckedIndexedAccess` already types it
  // as `number | undefined`; throwing on a miss turns a typo into an immediate
  // error instead of a blank, fixed-size badge (`.section-card__number` in
  // `components.css`) that would otherwise render wrong with nothing to say so.
  const number = dictionary.sectionNumber[section]
  if (number === undefined) throw new Error(`Unknown section: ${section}`)
  const labelKey = dictionary.sectionLabelKey(section)
  const headingId = `section-${section}`
  return (
    <section
      className={invalid ? 'section-card section-card--invalid' : 'section-card'}
      aria-labelledby={headingId}
    >
      <header className="section-card__header">
        <span className="section-card__number" aria-hidden="true">
          {number}
        </span>
        <h2 className="section-card__title" id={headingId}>
          {t(labelKey)}
        </h2>
        <span className="spacer" />
        {actions}
      </header>
      <div className="section-card__body">{children}</div>
    </section>
  )
}

/* ── FormField ──────────────────────────────────────────────────────────── */

export interface FormFieldProps {
  /** Template column name — also the dictionary key. */
  column: string
  /** react-hook-form path. Defaults to the column; consignments prefix it. */
  name?: string
  /** Key into `serverErrors`, when it differs from `name` (grouped consignments). */
  errorKey?: string
  full?: boolean
  disabled?: boolean
  autoFocus?: boolean
  /**
   * For a table cell, where the column header is the label. Hidden by the stylesheet, not by
   * `sr-only`: on a phone the table stacks into one card per row, the headers go, and each cell
   * shows its own label (`field__label--cell`, responsive/forms.css, #111).
   */
  hideLabel?: boolean
  /**
   * Prefix the party section — for a card that merges both parties, where the
   * short labels would otherwise be the same three words twice.
   */
  qualified?: boolean
  /**
   * Overrides the dictionary's `mandatory` **for this rendering only**.
   *
   * The goods dictionary never needs it: a goods field is required or it is not,
   * and nothing on screen changes that. The route sheet has two kinds of field
   * that are not fixed that way, and both are per-row rather than per-column, so
   * neither can be expressed by a dictionary entry:
   *
   *  - `servicio_paradas` and `servicio_clase_ref` are required by the **class
   *    chosen on that service row** (Art. 2(c) and 2(f), `isMandatoryForClass`);
   *  - `conductor_nombre`, `conductor_nif` and `matricula_autobus` are mandatory
   *    on the sheet and optional as a per-service override (BOE Sixth), so the
   *    same dictionary entry is rendered both ways on one screen.
   *
   * A prop rather than a `mandatory` computed inside the dictionary's `lookup`,
   * because `lookup` takes a column and knows nothing of which row it is being
   * asked about.
   */
  mandatory?: boolean
  /**
   * Leaves « (opcional)» off a field that is not required only because something else on the form
   * rules it out. One caller: the trailer's plate, while «Rígido» says there is no trailer (#228).
   * The field is disabled then, and a plate there is refused (`422 INVALID_VALUE`), so «opcional»
   * would tell the user they may fill it.
   *
   * Not a rule about disabled fields: a lock leaves an optional field optional, and it keeps its
   * mark.
   */
  hideOptionalMark?: boolean
  /**
   * Names this rendering after something narrower than the column.
   *
   * One caller today: Art. 2(f)'s identification field, which the Client asked to be labelled by
   * the class chosen on that service row — "the regular service being reinforced" for `REFUERZO`,
   * "the special-use regular transport" for `COLABORACION` (2026-09-17). It is one column with two
   * readings, not two fields, so it stays one dictionary entry and one payload key.
   *
   * ⚠️ The **printed** heading does not move with it: `render-passenger.ts` prints *Servicio
   * reforzado o colaborado* for every class, and `def.labelEs` remains that legal name — which is
   * what an English UI still shows beneath the label. Do not "align" the PDF to this.
   */
  labelKey?: string
  /**
   * Makes this field a type-ahead over the address book (#159): the party block it belongs to, and
   * whether it is the block's name or its NIF. Build it with `partyField`, which answers nothing
   * for the block's address — that field is filled by a choice, never searched.
   *
   * The field stays the same registered input; `PartyCombobox` wraps it. Every party block of both
   * forms passes it, so a reopened draft has it too, being the same form.
   *
   * Or a field of one other kind (#221): a place, a plate, or the driver (`bookField.ts`), which
   * `BookCombobox` wraps, with «Guardar en la libreta» beside it where it has one.
   */
  addressBook?: PartyField | BookField
}

export function FormField({
  column,
  name,
  errorKey,
  full,
  disabled,
  autoFocus,
  hideLabel,
  qualified,
  mandatory,
  hideOptionalMark,
  labelKey,
  addressBook,
}: FormFieldProps) {
  const { dictionary } = useContext(DecaFormContext)
  const def = dictionary.lookup(column)
  if (!def) throw new Error(`Unknown template column: ${column}`)
  // A choice writes the block by its columns, so a field wired to another block's columns would
  // fill that block from this one, with nothing on screen to say so. Refused out loud instead, as
  // an unknown column is above.
  if (addressBook && 'kind' in addressBook) {
    if (columnOf(addressBook) !== (name ?? column)) {
      throw new Error(`${name ?? column} is not the field of the ${addressBook.kind} it was given`)
    }
  } else if (addressBook && addressBook.columns[addressBook.field] !== (name ?? column)) {
    throw new Error(`${name ?? column} is not the ${addressBook.field} of the party block it was given`)
  }
  return (
    <BaseField
      def={def}
      name={name ?? column}
      errorKey={errorKey ?? name ?? column}
      full={full}
      disabled={disabled}
      autoFocus={autoFocus}
      hideLabel={hideLabel}
      qualified={qualified}
      mandatory={mandatory ?? def.mandatory}
      hideOptionalMark={hideOptionalMark}
      labelKey={labelKey}
      addressBook={addressBook}
    />
  )
}

function BaseField({
  def,
  name,
  errorKey,
  full,
  disabled,
  autoFocus,
  hideLabel,
  qualified,
  mandatory,
  hideOptionalMark,
  labelKey,
  addressBook,
}: {
  def: FieldDefLike | FieldDef
  name: string
  errorKey: string
  full?: boolean
  disabled?: boolean
  autoFocus?: boolean
  hideLabel?: boolean
  qualified?: boolean
  /** Already resolved against `def.mandatory` by `FormField`. */
  mandatory: boolean
  /** No « (opcional)» on a field something else rules out — see `FormFieldProps`. */
  hideOptionalMark?: boolean
  /** A narrower name for this rendering — see `FormFieldProps`. */
  labelKey?: string
  /** The address book's type-ahead — see `FormFieldProps`. */
  addressBook?: PartyField | BookField
}) {
  const { t, i18n } = useTranslation()
  const form = useFormContext()
  const { serverErrors, modified, lockedColumns } = useContext(DecaFormContext)
  const id = useId()

  const serverError = serverErrors[errorKey]
  const clientError = getNestedError(form.formState.errors, name)
  const isWarning = serverError?.severity === 'WARNING'
  const message = serverError?.message ?? clientError
  const invalid = Boolean(message) && !isWarning

  const locked = disabled || lockedColumns?.has(def.column)
  const isModified = modified?.has(def.column) ?? false

  // `party`, `control` and `maxLength` are goods-only — absent from the narrower
  // `FieldDefLike`, and so far from every other dictionary (the passenger one).
  // Each is guarded below by its *own* presence check, not by one shared
  // stand-in: the passenger dictionary already has two fields a `control` would
  // describe better than free text (`servicio_fecha`, a date; `servicio_clase`,
  // an enum over `SERVICE_CLASSES` — see `PASSENGER_FIELDS`), and the day it
  // gets one, a blanket `'control' in def` check would narrow a passenger def to
  // `FieldDef` with no compile error, since the match is purely structural — a
  // read of `.party` off that narrowed def would then silently be `undefined`.
  // `party` has no such plan, so guarding it on its own keeps the qualified
  // label correct regardless of what `control` grows into later. `control`
  // remains the right guard for the control branch below, because that really
  // is what it decides.
  //
  // Spanish is the document's language; in an English UI the fixed legal wording
  // is still shown, because that is what will be printed. `qualified` only means
  // anything for a def carrying `party` in the goods sense (`qualifiedFieldLabel`
  // reads its section) — silently ignored otherwise, but no caller currently
  // asks a non-goods def to qualify itself, so this never fires today.
  const label =
    qualified && 'party' in def ? qualifiedFieldLabel(def, t) : t(labelKey ?? `fields.${def.column}.label`)
  const showLegal = !i18n.language.startsWith('es') && label !== def.labelEs
  const help = t(`fields.${def.column}.help`, { defaultValue: '' })

  // «Obligatorio» and nothing more, on every field of a DeCA's form: ASGTRANS
  // asked for no «· responsable: Cargador» after it (2026-10-27).
  const requiredMessage = t('errors.requiredField')

  const registration = form.register(name, {
    required: mandatory ? requiredMessage : false,
    ...('control' in def && def.control === 'number'
      ? { valueAsNumber: true, min: { value: Number.MIN_VALUE, message: t('errors.positiveNumber') } }
      : {}),
    ...('maxLength' in def && def.maxLength
      ? { maxLength: { value: def.maxLength, message: t('errors.tooLong', { count: def.maxLength }) } }
      : {}),
  })

  const describedBy = [help || showLegal ? `${id}-help` : '', message ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ')

  const commonProps = {
    id,
    'aria-invalid': invalid,
    'aria-describedby': describedBy || undefined,
    disabled: locked,
    autoFocus,
    ...registration,
  }

  return (
    <div
      className={[
        'field',
        full ? 'field--full' : '',
        isModified ? 'field--modified' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <label className={hideLabel ? 'field__label field__label--cell' : 'field__label'} htmlFor={id}>
        <span>
          {label}
          {mandatory || hideOptionalMark ? null : (
            <span className="field__optional"> ({t('common.optional')})</span>
          )}
        </span>
      </label>

      {showLegal ? <span className="field__legal">{def.labelEs}</span> : null}

      {/* The address book's type-ahead first (#159): a party block's name or NIF, which
          is a text input in both dictionaries, rendered as the same input inside the
          combobox. Otherwise `'control' in def` is truthy for every goods field, so
          the next branch reproduces the original three-way choice unchanged; a
          dictionary without `control` (passenger, so far) falls back to its own
          `multiline` hint instead. See the note above `label` for why this stays its
          own guard rather than a shared stand-in. */}
      {addressBook && 'kind' in addressBook ? (
        <BookCombobox
          book={addressBook}
          label={label}
          multiline={'control' in def ? def.control === 'textarea' : Boolean(def.multiline)}
          input={{
            className: ('control' in def ? def.control === 'textarea' : def.multiline)
              ? 'textarea field__control'
              : 'input field__control',
            ...(('control' in def ? def.control === 'textarea' : def.multiline) ? { rows: 2 } : { type: 'text' }),
            maxLength: 'maxLength' in def ? def.maxLength : undefined,
            ...commonProps,
          }}
        />
      ) : addressBook ? (
        <PartyCombobox
          party={addressBook}
          input={{
            className: 'input field__control',
            type: 'text',
            maxLength: 'maxLength' in def ? def.maxLength : undefined,
            ...commonProps,
          }}
        />
      ) : 'control' in def ? (
        def.control === 'textarea' ? (
          <textarea className="textarea field__control" rows={2} maxLength={def.maxLength} {...commonProps} />
        ) : def.control === 'select' ? (
          <select className="select field__control" {...commonProps}>
            <option value="">{t('common.none')}</option>
            {UNIDADES_MEDIDA.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="input field__control"
            type={def.control === 'date' ? 'date' : def.control === 'number' ? 'number' : 'text'}
            // `inputMode`: a weight is often a decimal, and `type="number"` alone gets a keypad
            // without a separator on some phones (#111).
            {...(def.control === 'number' ? { step: 'any', min: 0, inputMode: 'decimal' as const } : {})}
            {...(def.control === 'date' ? SERVICE_DATE_BOUNDS : {})}
            maxLength={def.maxLength}
            {...commonProps}
          />
        )
      ) : def.multiline ? (
        <textarea className="textarea field__control" rows={2} {...commonProps} />
      ) : (
        <input className="input field__control" type="text" {...commonProps} />
      )}

      {/* "Never printed on the PDF" has to be visible on the field, not buried
          in a comment — but only once. `nif_conductor`'s own help text already
          says it, so the standalone note is the fallback for any future field
          that is captured and not rendered. */}
      {help ? (
        <span className="field__help" id={`${id}-help`}>
          {help}
        </span>
      ) : !def.onPdf ? (
        <span className="field__help" id={`${id}-help`}>
          <strong>{t('create.notPrinted')}.</strong>
        </span>
      ) : showLegal ? (
        <span className="field__help" id={`${id}-help`} />
      ) : null}

      {message ? (
        <span
          className={isWarning ? 'field__error field__error--warning' : 'field__error'}
          id={`${id}-error`}
        >
          <Icon name="alert" size={14} />
          {message}
        </span>
      ) : null}
    </div>
  )
}

/** RHF nests errors by path; `consignments.0.peso_magnitud` has to be walked. */
function getNestedError(errors: unknown, path: string): string | undefined {
  const segments = path.split('.')
  let cursor: unknown = errors
  for (const segment of segments) {
    if (typeof cursor !== 'object' || cursor === null) return undefined
    cursor = (cursor as Record<string, unknown>)[segment]
  }
  if (typeof cursor !== 'object' || cursor === null) return undefined
  const message = (cursor as { message?: unknown }).message
  return typeof message === 'string' ? message : undefined
}

/* ── MissingFieldsButton ────────────────────────────────────────────────── */

/**
 * How many fields are still missing, and the way to the list of them — the create forms' action
 * bar on a phone (#111).
 *
 * On a phone the rail sits after the whole form, and only its two buttons are pinned to the foot
 * of the screen. The list of what is missing stays in the rail, where the 422 is read, so this
 * takes the user there: `target` is a plain `tabIndex={-1}` wrapper around the rail's validation
 * card. Not the card's own `id`, which would make it a named `region` beside the four numbered
 * sections.
 *
 * `count` is what the rail is showing: the server's blocking failures once it has answered with a
 * 422 (`refused`, in the danger tone, since a 422 can arrive after the form's own count reached
 * nothing), the form's own count before. Rendered only when there is something to count, and
 * hidden above the compact width by responsive/forms.css, where the buttons are back in the rail's
 * Documento card beside the list itself.
 */
export function MissingFieldsButton({
  count,
  refused,
  target,
}: {
  count: number
  refused: boolean
  target: RefObject<HTMLElement | null>
}) {
  const { t } = useTranslation()
  if (count === 0) return null

  function jump() {
    const element = target.current
    // `?.` on the call: jsdom, and some embedded browsers, have no `scrollIntoView`, and the focus
    // must still move.
    element?.scrollIntoView?.({ block: 'start' })
    element?.focus({ preventScroll: true })
  }

  return (
    <Button
      variant={refused ? 'danger' : 'secondary'}
      icon="alert"
      className="create-actions__missing"
      aria-label={t('create.showMissing', { count })}
      onClick={jump}
    >
      {count}
    </Button>
  )
}

/* ── plain (non-dictionary) inputs ──────────────────────────────────────── */

export function TextInput({
  label,
  help,
  error,
  required,
  optional,
  full,
  ...rest
}: {
  label: string
  help?: string
  error?: string
  required?: boolean
  /**
   * Marks the field « (opcional)», as `FormField` marks an optional dictionary field (#140). The
   * marker is the input's, not the label's: a detail screen that shows the stored value under the
   * same catalogue key must not say it.
   */
  optional?: boolean
  full?: boolean
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className={full ? 'field field--full' : 'field'}>
      <label className="field__label" htmlFor={id}>
        <span>
          {label}
          {required ? (
            <span className="field__required" aria-label={t('common.requiredMark')}>
              {' *'}
            </span>
          ) : optional ? (
            <span className="field__optional"> ({t('common.optional')})</span>
          ) : null}
        </span>
      </label>
      <input
        id={id}
        className="input"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : help ? `${id}-help` : undefined}
        {...rest}
      />
      {help && !error ? (
        <span className="field__help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          <Icon name="alert" size={14} />
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function TextAreaInput({
  label,
  help,
  error,
  required,
  full,
  ...rest
}: {
  label: string
  help?: string
  error?: string
  required?: boolean
  full?: boolean
  /** React 19 passes `ref` as a prop, and `rest` takes it to the textarea. */
  ref?: React.Ref<HTMLTextAreaElement>
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className={full ? 'field field--full' : 'field'}>
      <label className="field__label" htmlFor={id}>
        <span>
          {label}
          {required ? (
            <span className="field__required" aria-label={t('common.requiredMark')}>
              {' *'}
            </span>
          ) : null}
        </span>
      </label>
      <textarea
        id={id}
        className="textarea"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : help ? `${id}-help` : undefined}
        {...rest}
      />
      {help && !error ? (
        <span className="field__help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          <Icon name="alert" size={14} />
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function SelectInput({
  label,
  help,
  error,
  required,
  full,
  children,
  ...rest
}: {
  label: string
  help?: string
  error?: string
  required?: boolean
  full?: boolean
  children: ReactNode
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className={full ? 'field field--full' : 'field'}>
      <label className="field__label" htmlFor={id}>
        <span>
          {label}
          {required ? (
            <span className="field__required" aria-label={t('common.requiredMark')}>
              {' *'}
            </span>
          ) : null}
        </span>
      </label>
      <select
        id={id}
        className="select"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : help ? `${id}-help` : undefined}
        {...rest}
      >
        {children}
      </select>
      {help && !error ? (
        <span className="field__help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          <Icon name="alert" size={14} />
          {error}
        </span>
      ) : null}
    </div>
  )
}
